import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import {
  MonthlyFrequencyOptions,
  ScheduleFrequencyOptions,
  ScheduleFrequencyType,
  WeeklyFrequencyOptions
} from 'src/app/models/scheduled-export';
import {
  DataService,
  Utilities,
  WorkflowContextService
} from 'src/app/services';

@Component({
    selector: 'wm-schedule-editor',
    templateUrl: './schedule-editor.component.html',
    styleUrls: ['./schedule-editor.component.css'],
    standalone: false
})
export class ScheduleEditorComponent implements OnInit, OnDestroy {
  @Input() type: ScheduleFrequencyType;
  @Input() options: ScheduleFrequencyOptions;
  @Input() form: UntypedFormGroup;
  @Input() existingNextExportOn: string;
  @Input() useMuiLayout?: boolean;

  @ViewChild('dStart', { static: false }) startDatePicker: NgbInputDatepicker;
  @ViewChild('dEnd', { static: false }) endDatePicker: NgbInputDatepicker;

  today = new Date();
  startDate: any;
  endDate: any;
  sendTime: any;
  clientTimeZoneOffset: number;
  clientTimeZoneCode: string;
  nextExportOn: string;
  gettingNextExport = false;
  ScheduleFrequencyType = ScheduleFrequencyType;

  // Added by Vipul Rathod on 22-02-2026
  count: string = '0';
  weekDays = [
    { key: 'onSunday', label: 'Sunday' },
    { key: 'onMonday', label: 'Monday' },
    { key: 'onTuesday', label: 'Tuesday' },
    { key: 'onWednesday', label: 'Wednesday' },
    { key: 'onThursday', label: 'Thursday' },
    { key: 'onFriday', label: 'Friday' },
    { key: 'onSaturday', label: 'Saturday' }
  ];

  monthlyDays = [
    { key: 'on1st', label: '1st' },
    { key: 'on2nd', label: '2nd' },
    { key: 'on3rd', label: '3rd' },
    { key: 'on4th', label: '4th' },
    { key: 'on5th', label: '5th' },
    { key: 'on6th', label: '6th' },
    { key: 'on7th', label: '7th' },
    { key: 'on8th', label: '8th' },
    { key: 'on9th', label: '9th' },
    { key: 'on10th', label: '10th' },
    { key: 'on11th', label: '11th' },
    { key: 'on12th', label: '12th' },
    { key: 'on13th', label: '13th' },
    { key: 'on14th', label: '14th' },
    { key: 'on15th', label: '15th' },
    { key: 'on16th', label: '16th' },
    { key: 'on17th', label: '17th' },
    { key: 'on18th', label: '18th' },
    { key: 'on19th', label: '19th' },
    { key: 'on20th', label: '20th' },
    { key: 'on21st', label: '21st' },
    { key: 'on22nd', label: '22nd' },
    { key: 'on23rd', label: '23rd' },
    { key: 'on24th', label: '24th' },
    { key: 'on25th', label: '25th' },
    { key: 'on26th', label: '26th' },
    { key: 'on27th', label: '27th' },
    { key: 'on28th', label: '28th' },
    { key: 'onLastDOM', label: 'Last day of month' }
  ];

  get monthlyDayColumns() {
    const width = window.innerWidth;
    let chunkSize;
    if (width < 480 && width > 340) {
      chunkSize = 7;
    } else{
      chunkSize = 5;
    }
    // const chunkSize = 10;
    const columns: any[] = [];

    for (let i = 0; i < this.monthlyDays.length; i += chunkSize) {
      columns.push(this.monthlyDays.slice(i, i + chunkSize));
    }

    return columns;
  }

  private structToDate(struct: any): Date | null {
    if (!struct) return null;
    return new Date(struct.year, struct.month - 1, struct.day);
  }

  private dateToStruct(date: any): any {
    if (!date) return null;

    if (date.year && date.month && date.day) {
      return date;
    }

    if (typeof date === 'string') {
      date = new Date(date);
    }

    if (date instanceof Date && !isNaN(date.getTime())) {
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      };
    }

    return null;
  }

  increaseCount() {
    if (this.options.frequency) {
      this.count = (Number(this.options.frequency)+1).toString();
    } else {
      this.count = (Number(this.count)+1).toString();
    }
    this.options.frequency = Number(this.count);
  }

  decreaseCount() {
    const currentValue = this.options.frequency ? Number(this.options.frequency) : Number(this.count);
    
    // Check if the current value is strictly greater than 1 before decrementing
    if (currentValue > 1) {
    if (this.options.frequency) {
      this.count = (Number(this.options.frequency)-1).toString();
    } else {
      this.count = (Number(this.count)-1).toString();
    }
    this.options.frequency = Number(this.count);
  }
  }

  toDate(time: any): Date | null {
    if (!time) return null;

    const date = new Date();
    date.setHours(time.hour || 0);
    date.setMinutes(time.minute || 0);
    date.setSeconds(time.second || 0);
    date.setMilliseconds(0);

    return date;
  }

  toTimeObject(date: Date) {
    if (!date) return null;

    return {
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds()
    };
  }
  // End by Vipul Rathod on 22-02-2026

  constructor(
    private _fb: UntypedFormBuilder,
    private _context: WorkflowContextService,
    private _dataSvc: DataService
  ) {}

  ngOnInit() {
    const client = this._context.client;
    this.clientTimeZoneOffset = client.timeZoneOffset;
    this.clientTimeZoneCode = client.timeZoneCode;

    // use a separate variable to bind to the time picker,
    // then merge the configured time back into the startDate on save
    if (this.options.startDateString) {
      const startDate = { year: 0, month: 0, day: 0 };
      const sendTime = { hour: 0, minute: 0, second: 0 };

      const sd = new Date(this.options.startDateString);

      startDate.year = sd.getFullYear();
      startDate.month = sd.getMonth() + 1;
      startDate.day = sd.getDate();

      sendTime.hour = sd.getHours();
      sendTime.minute = sd.getMinutes();
      sendTime.second = 0;

      this.startDate = startDate;
      this.sendTime = sendTime;
    } else {
      this.options.startDateString = Utilities.getDateTimeString(new Date());

      // set a default time
      this.sendTime = { hour: 12, minute: 0, second: 0 };
    }

    if (this.options.endDateString) {
      const endDate = { year: 0, month: 0, day: 0 };

      const ed = new Date(this.options.endDateString);

      endDate.year = ed.getFullYear();
      endDate.month = ed.getMonth() + 1;
      endDate.day = ed.getDate();

      this.endDate = endDate;
    }

    if (this.existingNextExportOn && this.existingNextExportOn !== '') {
      this.nextExportOn = this.existingNextExportOn;
    }

    if (!this.options.frequency) {
      this.options.frequency = 1;
    }

    this.form.addControl(
      'scheduleDaysSpecified',
      this._fb.control('', this.scheduleDaysSpecified.bind(this))
    );
    this.form.addControl(
      'startDate',
      this._fb.control(
        this.useMuiLayout
          ? this.structToDate(this.startDate)
          : this.startDate,
        Validators.required)
    );
    this.form.addControl(
      'endDate',
      this._fb.control(
        this.useMuiLayout
          ? this.structToDate(this.endDate)
          : this.endDate,
        Validators.nullValidator)
    );
    if (!this.useMuiLayout) {
      this.form.addControl(
        'sendTime',
        // Added by Amisha on 2026-07-09.
        // Reason: keep the legacy non-Janus schedule editor behavior unchanged because the
        // time-field issue exists only in the Janus scheduled export flow.
        this._fb.control(this.sendTime, Validators.required)
      );
    } else {
      this.form.addControl(
        'sendTime',
        // Added by Amisha on 2026-07-09.
        // Reason: keep the MUI send time field optional as well so the Janus UI does not
        // display a required error message inside the field when time is not mandatory.
        this._fb.control(this.toDate(this.sendTime), Validators.nullValidator)
      );

      this.form.get('sendTime')?.valueChanges.subscribe((value: Date) => {
        this.sendTime = this.toTimeObject(value);
        this.refreshNextExportOn();
      });

      this.form.get('startDate')?.valueChanges.subscribe(() => {
        this.refreshNextExportOn();
      });

      this.form.get('endDate')?.valueChanges.subscribe(() => {
        this.refreshNextExportOn();
      });
    }

  }

  ngOnDestroy() {
    this.form.removeControl('scheduleDaysSpecified');
    this.form.removeControl('startDate');
    this.form.removeControl('endDate');
    this.form.removeControl('sendTime');
  }

  scheduleDaysSpecified(): ValidationErrors | null {
    let castedOptions: any;

    if (this.options.type === 'weekly-frequency-options') {
      castedOptions = this.options as WeeklyFrequencyOptions;

      if (
        castedOptions.onSunday ||
        castedOptions.onMonday ||
        castedOptions.onTuesday ||
        castedOptions.onWednesday ||
        castedOptions.onThursday ||
        castedOptions.onFriday ||
        castedOptions.onSaturday
      ) {
        return null;
      } else {
        return { weekDaysSpecified: true };
      }
    }

    if (this.options.type === 'monthly-frequency-options') {
      castedOptions = this.options as MonthlyFrequencyOptions;

      if (
        castedOptions.on1st ||
        castedOptions.on2nd ||
        castedOptions.on3rd ||
        castedOptions.on4th ||
        castedOptions.on5th ||
        castedOptions.on6th ||
        castedOptions.on7th ||
        castedOptions.on8th ||
        castedOptions.on9th ||
        castedOptions.on10th ||
        castedOptions.on11th ||
        castedOptions.on12th ||
        castedOptions.on13th ||
        castedOptions.on14th ||
        castedOptions.on15th ||
        castedOptions.on16th ||
        castedOptions.on17th ||
        castedOptions.on18th ||
        castedOptions.on19th ||
        castedOptions.on20th ||
        castedOptions.on21st ||
        castedOptions.on22nd ||
        castedOptions.on23rd ||
        castedOptions.on24th ||
        castedOptions.on25th ||
        castedOptions.on26th ||
        castedOptions.on27th ||
        castedOptions.on28th ||
        castedOptions.onLastDOM
      ) {
        return null;
      } else {
        return { monthDaysSpecified: true };
      }
    }
  }

  updateScheduleDaysSpecified() {
    // the validator function needs re-bound because for some reason upon destroying and re-initting this component
    // this doesn't stay bound to the validator function and will return validation errors on updateValueAndValidity
    this.form
      .get('scheduleDaysSpecified')
      .setValidators(this.scheduleDaysSpecified.bind(this));
    this.form.get('scheduleDaysSpecified').updateValueAndValidity();
    this.form.get('scheduleDaysSpecified').markAsTouched();

    this.refreshNextExportOn();
  }

  getOptions(): ScheduleFrequencyOptions {
    this.setStartEndDates();

    return this.options;
  }

  dateObjIsValid(dateObj: any): boolean {
    return (
      dateObj &&
      dateObj.hasOwnProperty('year') &&
      dateObj.hasOwnProperty('month') &&
      dateObj.hasOwnProperty('day')
    );
  }

  setStartEndDates() {
    if (this.dateObjIsValid(this.startDate)) {
      const startDate = new Date(this.options.startDateString);

      startDate.setFullYear(
        this.startDate.year,
        this.startDate.month - 1,
        this.startDate.day
      );

      startDate.setHours(this.sendTime.hour, this.sendTime.minute, 0);

      this.options.startDateString = Utilities.getDateTimeString(startDate);
    }

    if (this.dateObjIsValid(this.endDate)) {
      const endDate = new Date();

      endDate.setFullYear(
        this.endDate.year,
        this.endDate.month - 1,
        this.endDate.day
      );

      endDate.setHours(0, 0, 0);

      this.options.endDateString = Utilities.getDateTimeString(endDate);
    } else {
      this.options.endDateString = null;
    }
  }

  convertFrequencyOptionsToWeekly() {
    this.type = ScheduleFrequencyType.Weekly;

    this.options = new WeeklyFrequencyOptions({
      startDateString: this.options.startDateString,
      endDateString: this.options.endDateString,
      frequency: this.options.frequency
    });

    this.form.get('scheduleDaysSpecified').updateValueAndValidity();
  }

  convertFrequencyOptionsToMonthly() {
    this.type = ScheduleFrequencyType.Monthly;

    this.options = new MonthlyFrequencyOptions({
      startDateString: this.options.startDateString,
      endDateString: this.options.endDateString,
      frequency: this.options.frequency
    });

    this.form.get('scheduleDaysSpecified').updateValueAndValidity();
  }

  onFocusStartDate() {
    this.startDatePicker.open();
    return true;
  }

  onFocusEndDate() {
    this.endDatePicker.open();
    return true;
  }

  refreshNextExportOn() {
    if (this.form.valid) {
      // Added by Vipul on 27/02/2026
      const startVal = this.form.controls['startDate'].value;
      const endVal = this.form.controls['endDate'].value;

      if (this.useMuiLayout) {
        this.startDate = this.dateToStruct(startVal);
        this.endDate = this.dateToStruct(endVal);
      } else {
        this.startDate = startVal;
        this.endDate = endVal;
      }
      // End by Vipul on 27/02/2026
      this.calculateNextExportOn();
    }
  }

  calculateNextExportOn() {
    this.gettingNextExport = true;

    const options = this.getOptions();

    this._dataSvc.calculateNextExportOn(options).subscribe(neo => {
      this.nextExportOn = neo;

      this.gettingNextExport = false;
    });
  }

  // Added by Vipul Rathod on 22-02-2026 to handle frequency change from radio button in MUI layout
  onFrequencyChange(value: Number) {
    if (value === 1) {
      this.convertFrequencyOptionsToWeekly();
    }

    if (value === 2) {
      this.convertFrequencyOptionsToMonthly();
    }
  }
  // End by Vipul Rathod on 22-02-2026 to handle frequency change from radio button in MUI layout
}
